import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  Tabs,
  Tab,
  Checkbox,
  FormGroup,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Button,
  ListItemIcon,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CompareArrows as CompareArrowsIcon,
  Compare as CompareIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { SECTION_CATEGORIES, STANDARDS, AVAILABILITY_STATUS } from '../../data/sectionTypes';
import { SAMPLE_SECTIONS } from '../../data/sampleSections';
import SectionDrawing from './SectionDrawing';
import PFCSectionProperties from './PFCSectionProperties';
import ISectionProperties from './ISectionProperties';

export default function SectionLibrary() {
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedStandard, setSelectedStandard] = useState(STANDARDS.AUST300);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [availabilityFilter, setAvailabilityFilter] = useState({
    [AVAILABILITY_STATUS.COMMON]: true,
    [AVAILABILITY_STATUS.SPECIAL]: true,
    [AVAILABILITY_STATUS.LEGACY]: false,
    [AVAILABILITY_STATUS.OBSOLETE]: false,
  });
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparedSections, setComparedSections] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
  };

  const handleCategoryToggle = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleAvailabilityChange = (status) => {
    setAvailabilityFilter(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Section Library</Typography>
          <Button
            variant="outlined"
            onClick={() => setComparisonMode(!comparisonMode)}
            startIcon={comparisonMode ? <CompareArrowsIcon /> : <CompareIcon />}
          >
            {comparisonMode ? 'Exit Compare' : 'Compare Sections'}
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Left Panel - Section List */}
        <Grid item xs={3}>
          <Paper sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search sections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Tabs
              value={selectedStandard}
              onChange={(e, newValue) => setSelectedStandard(newValue)}
              sx={{ mb: 2 }}
            >
              {Object.values(STANDARDS).map(standard => (
                <Tab key={standard} label={standard} value={standard} />
              ))}
            </Tabs>

            <List component="nav">
              {Object.entries(SECTION_CATEGORIES).map(([key, category]) => {
                const filteredSections = SAMPLE_SECTIONS
                  .filter(section => 
                    section.category === category && 
                    section.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                    section.standard === selectedStandard
                  );
                
                if (filteredSections.length === 0) return null;
                
                return (
                  <React.Fragment key={key}>
                    <ListItem button onClick={() => handleCategoryToggle(key)}>
                      <ListItemText primary={category} />
                      <IconButton edge="end" size="small">
                        {expandedCategories[key] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                      </IconButton>
                    </ListItem>
                    <Collapse in={expandedCategories[key]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {filteredSections.map(section => (
                          <ListItem
                            key={section.id}
                            button
                            selected={comparisonMode 
                              ? comparedSections.includes(section.id)
                              : selectedSection?.id === section.id
                            }
                            onClick={() => {
                              if (comparisonMode) {
                                if (comparedSections.includes(section.id)) {
                                  setComparedSections(prev => prev.filter(id => id !== section.id));
                                } else if (comparedSections.length < 3) {  // Limit to 3 sections
                                  setComparedSections(prev => [...prev, section.id]);
                                }
                              } else {
                                handleSectionSelect(section);
                              }
                            }}
                            sx={{ 
                              pl: 3,  // Reduced from 4 to 3
                              py: 0.5  // Add vertical padding of 0.5 units to make items closer together
                            }}
                          >
                            <ListItemText primary={section.name} />
                            {comparisonMode && comparedSections.includes(section.id) && (
                              <ListItemIcon>
                                <CheckCircleIcon color="primary" />
                              </ListItemIcon>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        </Grid>

        {/* Right Panel - Section Properties */}
        <Grid item xs={9}>
          <Paper sx={{ p: 2 }}>
            {selectedSection ? (
              <Grid container spacing={2}>
                {/* Section Name - with updated styling */}
                <Grid item xs={12}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 3,
                      fontWeight: 'bold',  // Make it bold
                      pl: 2  // Add padding-left to align with table content
                    }}
                  >
                    {selectedSection.name}
                  </Typography>
                </Grid>
                
                {/* Properties Table - Conditional based on section type */}
                <Grid item xs={8}>
                  {selectedSection.category === SECTION_CATEGORIES.PARALLEL_FLANGE_CHANNELS ? (
                    <PFCSectionProperties section={selectedSection} />
                  ) : (
                    <ISectionProperties section={selectedSection} />
                  )}
                </Grid>

                {/* Section Drawing */}
                <Grid item xs={4}>
                  <SectionDrawing section={selectedSection} />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center">
                Select a section to view its properties
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Availability Filter */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <FormGroup row>
          {Object.entries(AVAILABILITY_STATUS).map(([key, status]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={availabilityFilter[status]}
                  onChange={() => handleAvailabilityChange(status)}
                />
              }
              label={status}
            />
          ))}
        </FormGroup>
      </Paper>
    </Box>
  );
} 